// Message Thread Component
// Displays messages in a conversation with real-time updates

import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, Alert } from '@mui/material';

// Services and utils
import { needsDateSeparator } from '../../utils/dateUtils';

// Components
import MessageComposer from './MessageComposer';
import MessageSkeleton from './MessageSkeleton';
import ThreadHeader from './ThreadHeader';
import MessageList from './MessageList';
import CareTeamChatHeader from './CareTeamChatHeader';
import CareTeamContextCard from './CareTeamContextCard';
import ChatNotificationPrompt from './ChatNotificationPrompt';
import LifelogPromotionToolbar from './LifelogPromotionToolbar';
import { useMessageLifelogPromotion } from '../../hooks/messaging/useMessageLifelogPromotion';
import { useMessageThreadMessages } from '../../hooks/messaging/useMessageThreadMessages';

const LIFELOG_PROMOTION_ROLES = new Set(['care_owner', 'care_partner', 'caregiver']);

/**
 * Main MessageThread component
 */
const MessageThread = ({
  conversation,
  currentUserId,
  onConversationUpdate,
  isMobile = false,
  contextChild = null,
  contextChildRole = null,
  onBack = null,
  onOpenDetails = null,
  onOpenMedication = null,
  onOpenUpdate = null,
  refreshKey = 0,
}) => {
  const { messages, loading, error, refreshError, clearRefreshError, appendMessage } = useMessageThreadMessages({
    conversationId: conversation?.id,
    currentUserId,
    onConversationUpdate,
    refreshKey,
  });

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageRef = useRef(null);
  /**
   * Handle new message sent
   */
  const handleMessageSent = (newMessage) => {
    appendMessage(newMessage);
    
    // Scroll to bottom after a short delay
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /**
   * Handle reply to message
   */
  const handleReply = (_message) => {
    // TODO: Implement reply functionality
  };

  const lifelogPromotion = useMessageLifelogPromotion({
    messages,
    conversation,
    child: contextChild,
    userId: currentUserId,
  });

  /**
   * Group messages by date and consecutive sender
   */
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const previousMessage = messages[index - 1];
      const needsDateSep = needsDateSeparator(message.createdAt, previousMessage?.createdAt);
      const isSameSender = previousMessage?.senderId === message.senderId;
      const isWithinTimeThreshold = previousMessage && 
        (new Date(message.createdAt) - new Date(previousMessage.createdAt)) < 300000; // 5 minutes

      // Add date separator if needed
      if (needsDateSep) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        groups.push({
          type: 'date',
          date: message.createdAt,
          id: `date-${message.createdAt}`
        });
        currentGroup = null;
      }

      // Start new group or add to existing
      if (!currentGroup || !isSameSender || !isWithinTimeThreshold) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          type: 'messages',
          senderId: message.senderId,
          senderName: message.senderName,
          messages: [message],
          id: `group-${message.id}`
        };
      } else {
        currentGroup.messages.push(message);
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  }, [messages]);

  // Auto-scroll to bottom for new messages
  useEffect(() => {
    if (messages.length > 0 && !loading) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.senderId === currentUserId || !lastMessageRef.current) {
        scrollToBottom();
      }
      lastMessageRef.current = lastMessage;
    }
  }, [messages, loading, currentUserId, scrollToBottom]);

  if (loading && messages.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {isMobile ? (
          <>
            <CareTeamChatHeader
              title={conversation?.title || 'Care circle chat'}
              subtitle={`${conversation?.participants?.length || 0} members`}
              onBack={onBack}
              onInfoClick={onOpenDetails}
              onMenuClick={undefined}
            />
            <CareTeamContextCard child={contextChild} role={contextChildRole} onOpenDetails={onOpenDetails} />
          </>
        ) : (
          <ThreadHeader conversation={conversation} loading />
        )}

        {/* Loading messages */}
        <Box sx={{ flex: 1, p: 2, overflow: 'auto', bgcolor: isMobile ? '#F8FBFA' : 'grey.50' }}>
          <MessageSkeleton />
          <MessageSkeleton isOwnMessage />
          <MessageSkeleton />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {isMobile ? (
          <>
            <CareTeamChatHeader
              title={conversation?.title || 'Care circle chat'}
              subtitle={`${conversation?.participants?.length || 0} members`}
              onBack={onBack}
              onInfoClick={onOpenDetails}
              onMenuClick={undefined}
            />
            <CareTeamContextCard child={contextChild} role={contextChildRole} onOpenDetails={onOpenDetails} />
          </>
        ) : null}
        <Box sx={{ flex: 1, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isMobile ? '#F8FBFA' : 'transparent' }}>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            Failed to load messages: {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {isMobile ? (
        <>
          <CareTeamChatHeader
            title={conversation?.title || 'Care circle chat'}
            subtitle={`${conversation?.participants?.length || 0} members`}
            onBack={onBack}
            onInfoClick={onOpenDetails}
            onMenuClick={undefined}
          />
          <CareTeamContextCard child={contextChild} role={contextChildRole} onOpenDetails={onOpenDetails} />
          <ChatNotificationPrompt />
        </>
      ) : (
        <ThreadHeader conversation={conversation} />
      )}

      {/* Messages */}
      {lifelogPromotion.promotionError && (
        <Alert severity="error" onClose={lifelogPromotion.clearPromotionError} sx={{ mx: 2, mt: 1 }}>
          {lifelogPromotion.promotionError}
        </Alert>
      )}
      {refreshError && (
        <Alert severity="warning" onClose={clearRefreshError} sx={{ mx: 2, mt: 1 }}>
          {refreshError}
        </Alert>
      )}
      {LIFELOG_PROMOTION_ROLES.has(contextChildRole) && messages.length >= 2 && (
        <LifelogPromotionToolbar
          selectionMode={lifelogPromotion.selectionMode}
          selectedCount={lifelogPromotion.selectedCount}
          isAdding={lifelogPromotion.addingMessageId === 'message-set'}
          addFailed={lifelogPromotion.failedPromotionId === 'message-set'}
          onBeginSelection={lifelogPromotion.beginSelection}
          onCancelSelection={lifelogPromotion.cancelSelection}
          onAddSelected={lifelogPromotion.addSelected}
        />
      )}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          px: isMobile ? 2 : 2,
          pt: isMobile ? 1.5 : 2,
          pb: isMobile ? 2 : 2,
          backgroundColor: isMobile ? '#F8FBFA' : 'grey.50',
        }}
      >
        {groupedMessages.length === 0 ? (
          // Empty state
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 1.5,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <Box sx={{ fontSize: '3rem', opacity: 0.3 }}>💬</Box>
            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 800 }}>
              No messages yet
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.68, maxWidth: 280 }}>
              Send a message or share an update with the care circle.
            </Typography>
          </Box>
        ) : (
          <MessageList
            groupedMessages={groupedMessages}
            currentUserId={currentUserId}
            onReply={handleReply}
            onAddToLifelog={lifelogPromotion.addOne}
            onRetryLifelog={lifelogPromotion.retryOne}
            lifelogNavigationState={{
              returnTo: `/messages?childId=${encodeURIComponent(contextChild?.id || conversation?.childId || '')}&conversationId=${encodeURIComponent(conversation?.id || '')}`,
              returnLabel: 'Back to chat',
            }}
            canAddToLifelog={LIFELOG_PROMOTION_ROLES.has(contextChildRole)}
            addingMessageId={lifelogPromotion.addingMessageId}
            addedMessageIds={lifelogPromotion.addedMessageIds}
            failedPromotionId={lifelogPromotion.failedPromotionId}
            promotionStates={lifelogPromotion.promotionStates}
            selectionMode={lifelogPromotion.selectionMode}
            selectedMessageIds={lifelogPromotion.selectedMessageIds}
            selectionAtLimit={lifelogPromotion.selectionAtLimit}
            onToggleSelection={lifelogPromotion.toggleSelection}
            endRef={messagesEndRef}
          />
        )}
      </Box>

      {/* Message Composer */}
      <MessageComposer
        conversationId={conversation?.id}
        childId={conversation?.childId}
        currentUserId={currentUserId}
        onMessageSent={handleMessageSent}
        isMobile={isMobile}
        onQuickActionMedication={onOpenMedication}
        onQuickActionUpdate={onOpenUpdate}
      />
    </Box>
  );
};

export default MessageThread;
